import type { MouseEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import LanguageSelector from "../../../shared/components/layout/LanguageSelector";
import { useLandingAuthCta } from "../hooks/useLandingAuthCta";

const NAV_SECTIONS = [
  { id: "features", labelKey: "nav.features", index: "01" },
  { id: "how", labelKey: "nav.how", index: "02" },
  { id: "pricing", labelKey: "nav.pricing", index: "03" },
];

export function LandingHeader() {
  const { t } = useTranslation("landing");
  const cta = useLandingAuthCta();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // On the landing page smooth-scroll to the section; elsewhere let the
  // `/#id` href navigate home. Always close the mobile menu on click.
  const handleSectionClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `/#${id}`);
    }
  };

  // Lock body scroll + close on Esc while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-40 border-b border-[#1B2622] bg-[#070A0B]/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1320px] items-center gap-6 px-6 lg:px-10">
        <Link to="/" className="flex items-center" aria-label="Forecast Engine — home">
          <img
            src="/logo-header.svg"
            alt="Forecast Engine"
            width={150}
            height={45}
            className="h-10 w-auto"
          />
        </Link>

        {/* Desktop nav (md+) */}
        <nav className="ml-auto hidden items-center gap-8 md:flex" aria-label="Primary">
          {NAV_SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`/#${s.id}`}
              onClick={(e) => handleSectionClick(e, s.id)}
              className="group flex items-center gap-2 bp-mono text-[12px] uppercase tracking-[0.14em] text-[#788A82] transition-colors hover:text-[#E2EDE7]"
            >
              <span className="text-[#4CD6C4]/60 transition-colors group-hover:text-[#4CD6C4]">
                {s.index}
              </span>
              {t(s.labelKey)}
            </a>
          ))}
        </nav>

        {/* Desktop right cluster (md+) */}
        <div className="hidden items-center gap-4 md:ml-2 md:flex">
          <LanguageSelector />
          {cta.secondary && (
            <Link
              to={cta.secondary.href}
              className="bp-mono text-[12px] uppercase tracking-[0.12em] text-[#788A82] transition-colors hover:text-[#E2EDE7]"
            >
              {t(cta.secondary.labelKey)}
            </Link>
          )}
          <Link
            to={cta.primary.href}
            className="bp-btn inline-flex h-9 items-center px-4 text-[12px]"
          >
            {t(cta.primary.labelKey)}
          </Link>
        </div>

        {/* Mobile hamburger (<md) */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          aria-expanded={menuOpen}
          aria-controls="landing-mobile-menu"
          className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#1B2622] text-[#E2EDE7] md:hidden"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {menuOpen ? (
              <path d="M6 6l12 12M18 6L6 18" />
            ) : (
              <>
                <path d="M3 6h18" />
                <path d="M3 12h18" />
                <path d="M3 18h18" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu (<md) */}
      {menuOpen && (
        <div id="landing-mobile-menu" className="md:hidden">
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setMenuOpen(false)}
            className="fixed inset-x-0 bottom-0 top-16 z-30 bg-black/40"
          />
          <div className="relative z-40 border-t border-[#1B2622] bg-[#070A0B] px-6 py-5">
            <nav className="flex flex-col gap-3" aria-label="Primary mobile">
              {NAV_SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`/#${s.id}`}
                  onClick={(e) => handleSectionClick(e, s.id)}
                  className="flex items-center gap-2 bp-mono text-[13px] uppercase tracking-[0.14em] text-[#E2EDE7]"
                >
                  <span className="text-[#4CD6C4]/70">{s.index}</span>
                  {t(s.labelKey)}
                </a>
              ))}
            </nav>
            <div className="mt-5 flex items-center justify-between gap-4 border-t border-[#1B2622] pt-5">
              <LanguageSelector />
              {cta.secondary && (
                <Link
                  to={cta.secondary.href}
                  onClick={() => setMenuOpen(false)}
                  className="bp-mono text-[12px] uppercase tracking-[0.12em] text-[#788A82] hover:text-[#E2EDE7]"
                >
                  {t(cta.secondary.labelKey)}
                </Link>
              )}
            </div>
            <Link
              to={cta.primary.href}
              onClick={() => setMenuOpen(false)}
              className="bp-btn mt-5 inline-flex h-10 w-full items-center justify-center text-[13px]"
            >
              {t(cta.primary.labelKey)}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export default LandingHeader;
